import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Upload } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useSiteContent, deepMerge } from '../../context/SiteContentContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export const setIn = (obj, path, value) => {
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
    cur = cur[k];
  }
  cur[path[path.length - 1]] = value;
  return clone;
};

export const getIn = (obj, path) => path.reduce((o, k) => (o == null ? undefined : o[k]), obj);

// Module-level field components (stable identity → inputs keep focus while typing)
export const Text = ({ label, value, onChange, area, testid }) => (
  <div className="mb-4">
    <Label className="text-xs text-gray-500">{label}</Label>
    {area
      ? <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testid} />
      : <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testid} />}
  </div>
);

export const Lines = ({ label, value, onChange, testid }) => (
  <div className="mb-4">
    <Label className="text-xs text-gray-500">{label} (one per line)</Label>
    <Textarea value={(value || []).join('\n')} onChange={(e) => onChange(e.target.value.split('\n'))} className="mt-1 min-h-[120px]" data-testid={testid} />
  </div>
);

export const ImageField = ({ label, value, onChange, onUpload, testid }) => (
  <div className="mb-4">
    <Label className="text-xs text-gray-500">{label}</Label>
    <div className="flex items-center gap-3 mt-1">
      {value ? <img src={value} alt="preview" className="w-16 h-16 rounded-lg object-cover border bg-white" /> : null}
      <label className="cursor-pointer inline-flex items-center gap-2 text-sm rounded-full border border-[#003366] text-[#003366] px-4 py-2 hover:bg-[#003366] hover:text-white transition-colors">
        <Upload className="w-4 h-4" /> Upload
        <input type="file" accept="image/*" className="hidden" data-testid={testid} onChange={(e) => onUpload(e.target.files?.[0])} />
      </label>
    </div>
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="or paste image URL" className="mt-2 text-xs" />
  </div>
);

export const useCms = (key, defaults) => {
  const { refresh } = useSiteContent();
  const { toast } = useToast();
  const [content, setContent] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const cfg = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  useEffect(() => {
    axios.get(`${API}/content/${key}`).then((r) => setContent(deepMerge(defaults, r.data?.data || {}))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const update = (path, value) => setContent((prev) => setIn(prev, path, value));

  const uploadImage = async (path, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/content/upload`, fd, { headers: { ...cfg().headers } });
      update(path, r.data.url);
      toast({ title: 'Image uploaded', description: 'Remember to Save changes.' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Try a smaller JPG/PNG.', variant: 'destructive' });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/${key}`, { data: content }, cfg());
      toast({ title: 'Content saved ✓', description: 'Your changes are now live.' });
      refresh();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return { content, update, uploadImage, save, saving };
};
